// Cache de imagens (capas de viagem) pra offline, via @capacitor/filesystem.
//
// Decisão de produto (PLANO_OFFLINE.md):
//  - ONLINE: usa a URL remota normalmente E baixa a imagem pro Filesystem em
//    background (fire-and-forget), pra ter ela offline depois.
//  - OFFLINE: lê a imagem do cache local; se não houver, o caller mostra um
//    placeholder (nunca imagem quebrada).
//
// Degradação: fora do app (web/dev) ou sem o plugin nativo, tudo vira no-op e o
// caller usa a URL remota direto — zero quebra no site. Todos os imports do
// plugin são dinâmicos e try/catch.

import { Capacitor } from '@capacitor/core';

const SUBDIR = 'covers';
let _fs = null;

function isNative() {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
}
export function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

async function fsMod() {
  if (_fs) return _fs;
  _fs = await import('@capacitor/filesystem');
  return _fs;
}

// Nome de arquivo estável e seguro a partir da chave (id da viagem) + URL.
function pathFor(key, url) {
  const base = String(key || url || '').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 90);
  const ext = (String(url).match(/\.(jpe?g|png|webp|gif)(?:\?|$)/i)?.[1] || 'jpg').toLowerCase();
  return `${SUBDIR}/${base}.${ext}`;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/** Grava um blob já obtido no cache local. No-op fora do app. */
export async function cacheCoverBlob(url, key, blob) {
  if (!isNative() || !url || !blob) return;
  try {
    const { Filesystem, Directory } = await fsMod();
    const path = pathFor(key, url);
    const data = await blobToBase64(blob);
    await Filesystem.writeFile({ path, data, directory: Directory.Data, recursive: true });
  } catch (e) {
    console.warn('[imageCache] gravação falhou:', e?.message || e);
  }
}

/**
 * Garante a imagem cacheada (baixa se faltar). Fire-and-forget. `fetcher`
 * permite passar authFetch pra capas auth-gated (upload R2). No-op fora do app.
 */
export async function cacheCover(url, key, fetcher) {
  if (!isNative() || !url || isOffline()) return;
  try {
    const { Filesystem, Directory } = await fsMod();
    const path = pathFor(key, url);
    try { await Filesystem.stat({ path, directory: Directory.Data }); return; } catch { /* falta → baixa */ }
    const res = await (fetcher ? fetcher(url) : fetch(url));
    if (!res?.ok) return;
    await cacheCoverBlob(url, key, await res.blob());
  } catch (e) {
    console.warn('[imageCache] cache falhou:', e?.message || e);
  }
}

/** src local utilizável em <img> pra imagem cacheada, ou null se não houver. */
export async function cachedCoverSrc(url, key) {
  if (!isNative() || !url) return null;
  try {
    const { Filesystem, Directory } = await fsMod();
    const { uri } = await Filesystem.getUri({ path: pathFor(key, url), directory: Directory.Data });
    return Capacitor.convertFileSrc(uri);
  } catch {
    return null;
  }
}

// Placeholder (gradiente) quando não há imagem nem cache offline. Injeta o
// estilo uma vez; some com a imagem quebrada.
let _styleInjected = false;
function injectPlaceholderStyle() {
  if (_styleInjected || typeof document === 'undefined') return;
  _styleInjected = true;
  const s = document.createElement('style');
  s.id = 'coverCacheStyle';
  s.textContent = `.cover-cache-missing{background:linear-gradient(135deg,var(--accent-light,#e7eef7),var(--bg-card,#eef0f3))!important;}`;
  document.head.appendChild(s);
}
export function applyCoverPlaceholder(img) {
  if (!img) return;
  injectPlaceholderStyle();
  img.removeAttribute('src'); // evita ícone de imagem quebrada
  img.parentElement?.classList.add('cover-cache-missing');
}

/**
 * Liga o cache reativo num <img> que já vai receber `url` (capa remota):
 *  - online: cacheia em background;
 *  - se a imagem falhar (offline): tenta o cache local; se faltar, placeholder.
 * Não mexe no `img.src` (o caller já setou) — é puramente aditivo.
 */
export function wireCover(img, url, key, { fetcher } = {}) {
  if (!img || !url) return;
  cacheCover(url, key, fetcher); // fire-and-forget
  img.addEventListener('error', async () => {
    const local = await cachedCoverSrc(url, key);
    if (local) img.src = local;
    else applyCoverPlaceholder(img);
  }, { once: true });
}
